import React from 'react';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { I18n } from '@fiverr-private/i18n-react';
import { Domingo } from '@fiverr-private/domingo';
import { Section, Stack, Grid, Container } from '@fiverr-private/layout_components';
import { Heading, Link } from '@fiverr-private/typography';
import { LanguageSelectionModalEntry } from '@fiverr-private/locale_settings';
import { TERMS_URL, POLICY_URL, CURRENT_YEAR, SUPPORTED_LOCALES } from '../const';
import {
    AffiliateStore,
    Faq,
    HowToStartGuide,
    MarketingTools,
    ResourceGuides,
    AffiliatesLink,
    GetInspired,
    Blog,
    CommissionPlan,
    Login,
    ExternalLink,
    Forum,
} from '../shared/Link';

const Footer = () => {
    const { isMobile, url } = getContext();

    const proUrl = Domingo(url).removePath().pro().url;

    const templates = {
        policy: (text) => (
            <Link
                href={POLICY_URL}
                decoration={{ default: 'none', hover: 'underline' }}
                color={{ default: 'green_700', hover: 'green_700' }}
            >
                {text}
            </Link>
        ),
        terms: (text) => (
            <Link
                href={TERMS_URL}
                decoration={{ default: 'none', hover: 'underline' }}
                color={{ default: 'green_700', hover: 'green_700' }}
            >
                {text}
            </Link>
        ),
    };

    return (
        <Container as="footer" role="footer" borderTopWidth="sm" borderColor="grey_400" marginTop="3">
            <Section>
                {isMobile && (
                    <Stack justifyContent="center">
                        <LanguageSelectionModalEntry supportedLanguages={SUPPORTED_LOCALES} />
                    </Stack>
                )}
                <Grid gridTemplateColumns={{ default: '50%_50%', md: '25%_25%_25%_25%' }} gap="2">
                    <div>
                        <Heading
                            as="h6"
                            size="h_xxs"
                            color="grey_1100"
                            paddingTop={{ default: '8', md: '0' }}
                            paddingX="0"
                            paddingBottom="5"
                        >
                            <I18n k="affiliates_signup_perseus.affiliates.title" />
                        </Heading>
                        <nav>
                            <Stack as="ul" direction="column">
                                <Stack as="li" paddingBottom="4">
                                    <Link
                                        href="#"
                                        decoration={{ default: 'none', hover: 'underline' }}
                                        color="grey_900"
                                    >
                                        <I18n k="affiliates_signup_perseus.affiliates.join" />
                                    </Link>
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <Login variant="footer" />
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <CommissionPlan variant="footer" />
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <AffiliateStore variant="footer" />
                                </Stack>
                                <Stack as="li">
                                    <MarketingTools variant="footer" />
                                </Stack>
                            </Stack>
                        </nav>
                    </div>
                    <div>
                        <Heading
                            as="h6"
                            size="h_xxs"
                            color="grey_1100"
                            paddingTop={{ default: '8', md: '0' }}
                            paddingX="0"
                            paddingBottom="5"
                        >
                            <I18n k="affiliates_signup_perseus.support.title" />
                        </Heading>
                        <nav>
                            <Stack as="ul" direction="column">
                                <Stack as="li" paddingBottom="4">
                                    <Faq variant="footer" />
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <HowToStartGuide variant="footer" />
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <ResourceGuides variant="footer" />
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <ExternalLink
                                        href={pathfinder('content_pages_page', { page_slug: 'how-fiverr-works' })}
                                        category={'Buying on Fiverr'}
                                        variant="footer"
                                    >
                                        <I18n k="affiliates_signup_perseus.support.buying" />
                                    </ExternalLink>
                                </Stack>
                                <Stack as="li">
                                    <AffiliatesLink path="/contact-us/" category={'Contact us'} variant="footer">
                                        <I18n k="affiliates_signup_perseus.support.contact_us" />
                                    </AffiliatesLink>
                                </Stack>
                            </Stack>
                        </nav>
                    </div>
                    <div>
                        <Heading
                            as="h6"
                            size="h_xxs"
                            color="grey_1100"
                            paddingTop={{ default: '8', md: '0' }}
                            paddingX="0"
                            paddingBottom="5"
                        >
                            <I18n k="affiliates_signup_perseus.community.title" />
                        </Heading>
                        <Stack as="ul" direction="column">
                            <Stack as="li" paddingBottom="4">
                                <Blog variant="footer" />
                            </Stack>
                            <Stack as="li" paddingBottom="4">
                                <Forum variant="footer" />
                            </Stack>
                            <Stack as="li">
                                <GetInspired variant="footer" />
                            </Stack>
                        </Stack>
                    </div>
                    <div>
                        <Heading
                            as="h6"
                            size="h_xxs"
                            color="grey_1100"
                            paddingTop={{ default: '8', md: '0' }}
                            paddingX="0"
                            paddingBottom="5"
                        >
                            <I18n k="affiliates_signup_perseus.more_from_fiverr.title" />
                        </Heading>
                        <nav>
                            <Stack as="ul" direction="column">
                                <Stack as="li" paddingBottom="4">
                                    <ExternalLink href="/" category={'Fiverr'} variant="footer">
                                        <I18n k="affiliates_signup_perseus.more_from_fiverr.fiverr" />
                                    </ExternalLink>
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <ExternalLink href={proUrl} category={'Pro'} variant="footer">
                                        <I18n k="affiliates_signup_perseus.more_from_fiverr.pro" />
                                    </ExternalLink>
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <ExternalLink
                                        href="https://workspace.fiverr.com/"
                                        category={'AND_CO'}
                                        variant="footer"
                                    >
                                        <I18n k="affiliates_signup_perseus.more_from_fiverr.workspace" />
                                    </ExternalLink>
                                </Stack>
                                <Stack as="li" paddingBottom="4">
                                    <ExternalLink
                                        href="https://learn.fiverr.com/"
                                        category={'Learn from Fiverr'}
                                        variant="footer"
                                    >
                                        <I18n k="affiliates_signup_perseus.more_from_fiverr.learn_from_fiverr" />
                                    </ExternalLink>
                                </Stack>
                                <Stack as="li">
                                    <ExternalLink
                                        href="/news/press-releases"
                                        category={'Press Releases'}
                                        variant="footer"
                                    >
                                        <I18n k="affiliates_signup_perseus.more_from_fiverr.press_and_news" />
                                    </ExternalLink>
                                </Stack>
                            </Stack>
                        </nav>
                    </div>
                </Grid>
            </Section>
            <Section borderTopWidth="sm" borderColor="grey_400" backgroundColor="grey_100">
                <I18n
                    k="affiliates_signup_perseus.footer.all_rights"
                    templates={templates}
                    params={{ year: CURRENT_YEAR }}
                />
            </Section>
        </Container>
    );
};

export default Footer;
